import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext.hook';

/**
 * Interface para os dados da incorporadora
 */
export interface Incorporadora {
  id: string;
  cnpj: string;
  razao_social: string;
  nome_fantasia: string | null;
  contato_legal_nome: string | null;
  contato_legal_email: string | null;
  contato_legal_telefone: string | null;
  contato_financeiro_nome: string | null;
  contato_financeiro_email: string | null;
  contato_financeiro_telefone: string | null;
  endereco_cep: string | null;
  endereco_logradouro: string | null;
  endereco_numero: string | null;
  endereco_complemento: string | null;
  endereco_bairro: string | null;
  endereco_cidade: string | null;
  endereco_uf: string | null;
  logo_url: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

/**
 * Interface para criar/atualizar incorporadora
 */
export interface IncorporadoraInput {
  cnpj: string;
  razao_social: string;
  nome_fantasia?: string;
  contato_legal_nome?: string;
  contato_legal_email?: string;
  contato_legal_telefone?: string;
  contato_financeiro_nome?: string;
  contato_financeiro_email?: string;
  contato_financeiro_telefone?: string;
  endereco_cep?: string;
  endereco_logradouro?: string;
  endereco_numero?: string;
  endereco_complemento?: string;
  endereco_bairro?: string;
  endereco_cidade?: string;
  endereco_uf?: string;
}

/**
 * Hook para gerenciar a incorporadora do usuário logado
 */
export const useIncorporadora = () => {
  const [incorporadora, setIncorporadora] = useState<Incorporadora | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const { user, profile, refreshProfile } = useAuth();

  /**
   * Busca a incorporadora vinculada ao usuario
   */
  const fetchIncorporadora = useCallback(async () => {
    if (!user || !profile?.incorporadora_id) {
      setIncorporadora(null);
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('incorporadoras')
        .select('*')
        .eq('id', profile.incorporadora_id)
        .maybeSingle();

      if (fetchError) throw fetchError;

      setIncorporadora(data);
      return data;
    } catch (err) {
      console.error('Erro ao buscar incorporadora:', err);
      setError('Erro ao carregar dados da incorporadora');
      toast({
        title: 'Erro',
        description: 'Nao foi possivel carregar os dados da incorporadora.',
        variant: 'destructive',
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [user, profile?.incorporadora_id, toast]);

  /**
   * Cria uma nova incorporadora e vincula ao profile do usuario
   */
  const createIncorporadora = async (input: IncorporadoraInput): Promise<Incorporadora | null> => {
    if (!user) {
      toast({
        title: 'Erro',
        description: 'Usuario nao autenticado.',
        variant: 'destructive',
      });
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Gerar UUID client-side para evitar problema de RLS no RETURNING *
      // O INSERT passa pela policy de INSERT (role incorporador), mas o
      // RETURNING * (via .select()) exige policy de SELECT, que depende de
      // profiles.incorporadora_id — que so e preenchido APOS o insert.
      const newId = crypto.randomUUID();

      // Preparar dados para insercao
      const insertData = {
        id: newId,
        cnpj: input.cnpj,
        razao_social: input.razao_social,
        nome_fantasia: input.nome_fantasia || null,
        contato_legal_nome: input.contato_legal_nome || null,
        contato_legal_email: input.contato_legal_email || null,
        contato_legal_telefone: input.contato_legal_telefone || null,
        contato_financeiro_nome: input.contato_financeiro_nome || null,
        contato_financeiro_email: input.contato_financeiro_email || null,
        contato_financeiro_telefone: input.contato_financeiro_telefone || null,
        endereco_cep: input.endereco_cep || null,
        endereco_logradouro: input.endereco_logradouro || null,
        endereco_numero: input.endereco_numero || null,
        endereco_complemento: input.endereco_complemento || null,
        endereco_bairro: input.endereco_bairro || null,
        endereco_cidade: input.endereco_cidade || null,
        endereco_uf: input.endereco_uf || null,
        status: 'ativo',
      };

      // Criar incorporadora SEM .select() para evitar falha de SELECT RLS
      const { error: insertError } = await supabase
        .from('incorporadoras')
        .insert(insertData);

      if (insertError) {
        // Verificar se é erro de CNPJ duplicado
        if (insertError.code === '23505') {
          throw new Error('CNPJ ja cadastrado no sistema.');
        }
        throw insertError;
      }

      // Vincular ao profile do usuario ANTES de buscar a incorporadora
      // (necessario para que a SELECT policy "Users can view own incorporadora" funcione)
      const { error: updateProfileError } = await supabase
        .from('profiles')
        .update({
          incorporadora_id: newId,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', user.id);

      if (updateProfileError) {
        console.error('Erro ao vincular incorporadora ao perfil:', updateProfileError);
        // Tentar reverter criacao da incorporadora
        await supabase.from('incorporadoras').delete().eq('id', newId);
        throw new Error('Erro ao vincular incorporadora ao perfil.');
      }

      // Agora buscar a incorporadora completa (SELECT policy passa porque profile ja esta vinculado)
      const { data: newIncorporadora, error: fetchError } = await supabase
        .from('incorporadoras')
        .select('*')
        .eq('id', newId)
        .single();

      if (fetchError) {
        throw fetchError;
      }

      toast({
        title: 'Incorporadora cadastrada!',
        description: 'Os dados da sua empresa foram salvos com sucesso.',
      });

      setIncorporadora(newIncorporadora);

      // Atualizar o profile no AuthContext para refletir o novo incorporadora_id
      await refreshProfile();

      return newIncorporadora;
    } catch (err) {
      console.error('Erro ao criar incorporadora:', err);
      const errorMessage = err instanceof Error ? err.message : 'Erro ao cadastrar incorporadora.';
      setError(errorMessage);
      toast({
        title: 'Erro ao cadastrar',
        description: errorMessage,
        variant: 'destructive',
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Atualiza os dados de uma incorporadora existente
   */
  const updateIncorporadora = async (
    id: string,
    input: Partial<IncorporadoraInput>
  ): Promise<Incorporadora | null> => {
    if (!user) {
      toast({
        title: 'Erro',
        description: 'Usuario nao autenticado.',
        variant: 'destructive',
      });
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const updateData = {
        ...(input.cnpj && { cnpj: input.cnpj }),
        ...(input.razao_social && { razao_social: input.razao_social }),
        nome_fantasia: input.nome_fantasia ?? null,
        contato_legal_nome: input.contato_legal_nome ?? null,
        contato_legal_email: input.contato_legal_email ?? null,
        contato_legal_telefone: input.contato_legal_telefone ?? null,
        contato_financeiro_nome: input.contato_financeiro_nome ?? null,
        contato_financeiro_email: input.contato_financeiro_email ?? null,
        contato_financeiro_telefone: input.contato_financeiro_telefone ?? null,
        endereco_cep: input.endereco_cep ?? null,
        endereco_logradouro: input.endereco_logradouro ?? null,
        endereco_numero: input.endereco_numero ?? null,
        endereco_complemento: input.endereco_complemento ?? null,
        endereco_bairro: input.endereco_bairro ?? null,
        endereco_cidade: input.endereco_cidade ?? null,
        endereco_uf: input.endereco_uf ?? null,
        updated_at: new Date().toISOString(),
      };

      const { data: updatedData, error: updateError } = await supabase
        .from('incorporadoras')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (updateError) {
        // Verificar se é erro de CNPJ duplicado
        if (updateError.code === '23505') {
          throw new Error('CNPJ ja cadastrado no sistema.');
        }
        throw updateError;
      }

      toast({
        title: 'Dados atualizados!',
        description: 'Os dados da sua empresa foram atualizados com sucesso.',
      });

      setIncorporadora(updatedData);
      return updatedData;
    } catch (err) {
      console.error('Erro ao atualizar incorporadora:', err);
      const errorMessage = err instanceof Error ? err.message : 'Erro ao atualizar dados.';
      setError(errorMessage);
      toast({
        title: 'Erro ao atualizar',
        description: errorMessage,
        variant: 'destructive',
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Faz upload do logo da incorporadora
   */
  const uploadLogo = async (file: File): Promise<string | null> => {
    if (!user || !incorporadora) {
      toast({
        title: 'Erro',
        description: 'Incorporadora nao encontrada.',
        variant: 'destructive',
      });
      return null;
    }

    setIsLoading(true);

    try {
      // Definir nome do arquivo
      const fileExt = file.name.split('.').pop();
      const fileName = `${incorporadora.id}/logo.${fileExt}`;

      // Upload para o storage
      const { error: uploadError } = await supabase.storage
        .from('incorporadora-logos')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Obter URL publica
      const { data: urlData } = supabase.storage
        .from('incorporadora-logos')
        .getPublicUrl(fileName);

      const logoUrl = urlData.publicUrl;

      // Atualizar incorporadora com a URL do logo
      const { error: updateError } = await supabase
        .from('incorporadoras')
        .update({ logo_url: logoUrl, updated_at: new Date().toISOString() })
        .eq('id', incorporadora.id);

      if (updateError) throw updateError;

      setIncorporadora(prev => prev ? { ...prev, logo_url: logoUrl } : null);

      toast({
        title: 'Logo atualizado!',
        description: 'O logo da sua empresa foi atualizado com sucesso.',
      });

      return logoUrl;
    } catch (err) {
      console.error('Erro ao fazer upload do logo:', err);
      toast({
        title: 'Erro no upload',
        description: 'Nao foi possivel fazer upload do logo.',
        variant: 'destructive',
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Remove o logo da incorporadora
   */
  const removeLogo = async (): Promise<boolean> => {
    if (!user || !incorporadora) {
      return false;
    }

    setIsLoading(true);

    try {
      // Remover do storage (se existir)
      if (incorporadora.logo_url) {
        const fileName = `${incorporadora.id}/logo`;
        await supabase.storage
          .from('incorporadora-logos')
          .remove([`${fileName}.png`, `${fileName}.jpg`, `${fileName}.jpeg`, `${fileName}.webp`]);
      }

      // Atualizar incorporadora
      const { error: updateError } = await supabase
        .from('incorporadoras')
        .update({ logo_url: null, updated_at: new Date().toISOString() })
        .eq('id', incorporadora.id);

      if (updateError) throw updateError;

      setIncorporadora(prev => prev ? { ...prev, logo_url: null } : null);

      toast({
        title: 'Logo removido',
        description: 'O logo foi removido com sucesso.',
      });

      return true;
    } catch (err) {
      console.error('Erro ao remover logo:', err);
      toast({
        title: 'Erro',
        description: 'Nao foi possivel remover o logo.',
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Carregar incorporadora quando o profile mudar
  useEffect(() => {
    if (user && profile?.incorporadora_id) {
      fetchIncorporadora();
    } else {
      setIncorporadora(null);
    }
  }, [user, profile?.incorporadora_id, fetchIncorporadora]);

  /**
   * Verifica se um CNPJ já está cadastrado na tabela incorporadoras.
   * Retorna true se está disponível (pode cadastrar), false se já existe.
   */
  const checkCnpjAvailability = useCallback(async (cnpj: string): Promise<boolean> => {
    const digits = cnpj.replace(/\D/g, '');
    if (digits.length !== 14) return true; // CNPJ incompleto, não bloquear

    try {
      const { count, error: queryError } = await supabase
        .from('incorporadoras')
        .select('id', { count: 'exact', head: true })
        .eq('cnpj', cnpj);

      if (queryError) {
        console.warn('Erro ao verificar disponibilidade do CNPJ:', queryError);
        return true; // Em caso de erro de rede, não bloquear (o INSERT vai falhar depois se for duplicado)
      }

      return (count ?? 0) === 0;
    } catch {
      return true;
    }
  }, []);

  return {
    incorporadora,
    isLoading,
    error,
    fetchIncorporadora,
    createIncorporadora,
    updateIncorporadora,
    uploadLogo,
    removeLogo,
    checkCnpjAvailability,
  };
};
